import { createRecognitionRefinement, deleteRefinement } from '@kudos/http-client'
import { useApiError } from '@shared/errors/useApiError'
import { Selectable } from '@shared/filters/selections'
import useQualities from '@shared/hooks/useQualities'
import { useMutation } from '@tanstack/react-query'
import { isEmpty } from 'lodash'
import { useRef, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { v4 as uuidv4 } from 'uuid'
import useRecognitionModal from '../useRecognitionModal'
import { RecognitionFormData } from './useSendRecognitionForm'

const useRecognitionMessageRefinement = () => {
  const { dialogData } = useRecognitionModal()
  const [originalMessage, setOriginalMessage] = useState<string>('')
  const [originalQualities, setOriginalQualities] = useState<Selectable[]>([])
  const fallbackSessionIdRef = useRef<string | null>(null)

  const { control, setValue, setError } = useFormContext<RecognitionFormData>()
  const message = useWatch({ control, name: 'message' })
  const refinementId = useWatch({ control, name: 'refinementId' })
  const selectedQualities = useWatch({ control, name: 'qualities' })

  const { qualities: tenantQualities } = useQualities()

  const { handleApiError } = useApiError<RecognitionFormData>()

  const getSessionId = (): string => {
    const fromModal = dialogData?.refinementSessionId
    if (fromModal) return fromModal
    if (!fallbackSessionIdRef.current) {
      fallbackSessionIdRef.current = uuidv4()
    }
    return fallbackSessionIdRef.current
  }

  // Builds the `qualities` payload per SYN-7079 AC:
  // - If the tenant has no qualities available, pass an empty array.
  // - If the user has already selected qualities, pass those.
  // - Otherwise pass all tenant qualities so the AI can pick one.
  const buildQualitiesPayload = (): { id: string; name: string }[] => {
    if (isEmpty(tenantQualities)) return []
    const source = !isEmpty(selectedQualities) ? selectedQualities : (tenantQualities ?? [])
    return source.map(({ id, name }) => ({ id, name }))
  }

  const { isPending: isPendingRefineMessage, mutateAsync: refineMessage } = useMutation({
    mutationFn: async () => {
      if (isEmpty(refinementId)) {
        setOriginalMessage(message)
        setOriginalQualities(selectedQualities ?? [])
      }

      const userHadSelectedQualities = !isEmpty(selectedQualities)
      const data = await createRecognitionRefinement({
        message,
        sessionId: getSessionId(),
        qualities: buildQualitiesPayload(),
      })
      return { ...data, userHadSelectedQualities }
    },
    onSuccess: data => {
      setValue('refinementId', data.refinementId, { shouldValidate: true })
      setValue('message', data.output, { shouldValidate: true })

      // When the user hadn't selected qualities, auto-select the ones the AI chose.
      if (!data.userHadSelectedQualities && !isEmpty(data.qualityIds)) {
        const aiQualities = (data.qualityIds ?? [])
          .map(qualityId => tenantQualities?.find(quality => quality.id === qualityId))
          .filter((quality): quality is Selectable => !!quality)
        if (!isEmpty(aiQualities)) {
          setValue('qualities', aiQualities, { shouldValidate: true })
        }
      }
    },
    onError: error => {
      handleApiError(error, setError, ['message'])
    },
  })

  const { isPending: isPendingUndoRefinedMessage, mutateAsync: deleteRefinementMessage } =
    useMutation({
      mutationFn: async () => {
        return await deleteRefinement(refinementId ?? '')
      },
      onSuccess: () => {
        setValue('refinementId', undefined, { shouldValidate: true })
        setValue('message', originalMessage, { shouldValidate: true })
        setValue('qualities', originalQualities, { shouldValidate: true })
      },
      onError: error => {
        handleApiError(error)
      },
    })

  return {
    isPendingRefineMessage,
    refineMessage,
    isPendingUndoRefinedMessage,
    deleteRefinementMessage,
    originalMessage,
    hasRefined: !!refinementId,
  }
}

export default useRecognitionMessageRefinement
